import { X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/ui/sheet';

/**
 * Off-canvas navigation drawer (SL-0027, Activity 3), a thin wrapper over the shadcn `Sheet` /
 * Radix `Dialog` primitive (Resolution 16). Radix Dialog provides, with no hand-written code:
 * Escape-to-close, scrim click-to-close, a focus trap cycling `Tab`/`Shift+Tab` within the panel,
 * and initial focus on open. Focus RESTORATION on close is the one mechanism Radix does NOT
 * provide automatically here: its built-in restoration targets whatever element is wrapped in a
 * `Dialog.Trigger`, but this drawer is externally controlled by a plain header button (Task 3.2's
 * hamburger, deliberately outside this component's own tree, per its `{ open, onClose, children }`
 * API) — `context.triggerRef` therefore never populates and Radix's own restoration is a silent
 * no-op (confirmed empirically in `NavigationDrawer.test.tsx`). This effect + `onCloseAutoFocus`
 * pair replaces it: capture `document.activeElement` the moment the drawer opens (the hamburger,
 * since clicking it is what sets `open` to `true`), then explicitly refocus it on close.
 *
 * It carries its own CLOSE BUTTON. Escape and the scrim close it too, but a phone has no Escape key
 * and the scrim beside a 340px drawer on a 390px screen is a 50px strip; the proposal's phone menu
 * puts the way out in the top corner, where a person looks for it. It is a Radix `Close`, so the
 * focus restoration above applies to it exactly as to the other two ways out.
 */
export function NavigationDrawer({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation('shell');
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open && document.activeElement instanceof HTMLElement) {
      previouslyFocusedRef.current = document.activeElement;
    }
  }, [open]);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <SheetContent
        id="app-nav-drawer"
        side="left"
        className="p-0"
        onCloseAutoFocus={(event) => {
          if (previouslyFocusedRef.current) {
            event.preventDefault();
            previouslyFocusedRef.current.focus();
          }
        }}
      >
        <SheetTitle className="sr-only">{t('nav.menuTitle')}</SheetTitle>
        <SheetClose className="drawer-close" aria-label={t('nav.closeMenu')}>
          <X aria-hidden="true" />
        </SheetClose>
        {children}
      </SheetContent>
    </Sheet>
  );
}
